import type { IncomeRoll, RoundView, Standing } from '@guess/engine';
import type {
  BettingState,
  LobbyState,
  Phase,
  PlayerInfo,
  PowerCard,
  ServerMessage,
  ThemeInfo,
} from '@guess/protocol';

/**
 * L'etat du client, et la fonction PURE qui le fait avancer.
 *
 * Aucun appel reseau, aucune horloge, aucun DOM : `reduce(state, event)` ne
 * depend que de ses arguments. C'est le meme parti pris que le moteur et que
 * le salon, et il donne la meme chose : une partie entiere se rejoue dans un
 * test en quelques millisecondes, sans monter un seul composant React.
 *
 * Le client ne CALCULE rien. Il n'additionne pas un score, ne juge pas une
 * reponse, ne decide pas d'une cote : il range ce que le serveur lui pousse et
 * l'affiche. Y ajouter un calcul de jeu serait une faille, puisque ce code
 * tourne chez le joueur.
 */

export type ConnectionStatus = 'idle' | 'connecting' | 'joined' | 'closed';

/** Retour prive sur sa propre reponse. Les autres n'en savent rien. */
export interface AnswerFeedback {
  correct: boolean;
  delta: number;
  detail?: string;
  /** Jeton d'animation : change a chaque retour, meme identique au precedent. */
  token: number;
}

export interface ResultView {
  answer: string;
  subtitle?: string;
  /** Image nette : jamais la version pixelisee de la manche. */
  imageUrl?: string;
  notes: string[];
  standings: Standing[];
  bailedOut: string[];
}

export interface GameState {
  status: ConnectionStatus;
  /** Derniere erreur affichable. Effacee des que le joueur agit. */
  error: string | null;
  protocolVersion: number | null;

  /** Identite. `you` est l'identifiant que le serveur nous a attribue. */
  you: string | null;
  room: string | null;
  hostId: string | null;

  /**
   * La table. Conservee pour toute la partie : c'est la SEULE source qui
   * associe un identifiant a un nom, et le serveur ne rediffuse le salon
   * qu'en cas de changement de siege.
   */
  players: PlayerInfo[];
  readyIds: string[];
  catalogue: PowerCard[];
  loadoutSize: number;
  /** Tous les themes du catalogue de base, pour construire les filtres. */
  availableThemes: ThemeInfo[];
  /** Vide = aucun filtre, tous les themes sont joues. */
  selectedThemes: string[];
  /** Faux si l'hote a coupe les pouvoirs (bonus/malus) pour cette partie. */
  powersEnabled: boolean;

  phase: Phase;
  roundNumber: number;
  totalRounds: number;

  betting: BettingState | null;
  round: RoundView | null;
  income: IncomeRoll[] | null;
  feedback: AnswerFeedback | null;
  result: ResultView | null;
  finalStandings: Standing[] | null;

  /**
   * Echeance de la phase courante, en temps LOCAL (`performance.now`).
   *
   * Le serveur envoie un reste, pas une date : traduire ce reste en echeance
   * des la reception est ce qui permet au client d'animer un chrono fluide
   * entre deux messages, sans jamais tenter de deviner l'heure du serveur.
   */
  phaseEndsAt: number | null;
  phaseTotalMs: number | null;

  /** Incremente a chaque nouveau palier : sert de cle d'animation. */
  stageToken: number;
  /**
   * Echeance LOCALE de la fenetre de buzz en cours, meme principe que
   * `phaseEndsAt` mais pour ce chrono plus court. Null hors manche a buzzer
   * ou quand personne ne tient la main.
   */
  buzzEndsAt: number | null;
  /** Incremente a chaque manche : reinitialise les entrees de reponse. */
  roundToken: number;
  /** Vrai des qu'on a repondu cette manche : l'entree se verrouille. */
  submitted: boolean;
}

export const initialState: GameState = {
  status: 'idle',
  error: null,
  protocolVersion: null,
  you: null,
  room: null,
  hostId: null,
  players: [],
  readyIds: [],
  catalogue: [],
  loadoutSize: 2,
  availableThemes: [],
  selectedThemes: [],
  powersEnabled: true,
  phase: 'lobby',
  roundNumber: 0,
  totalRounds: 0,
  betting: null,
  round: null,
  income: null,
  feedback: null,
  result: null,
  finalStandings: null,
  phaseEndsAt: null,
  phaseTotalMs: null,
  stageToken: 0,
  buzzEndsAt: null,
  roundToken: 0,
  submitted: false,
};

/**
 * Les evenements. Un message du serveur en est un ; la vie de la socket et
 * les gestes du client en sont d'autres.
 *
 * `at` est l'instant LOCAL de reception (`performance.now`), fourni par
 * l'appelant. La fonction reste donc pure et le test controle le temps.
 */
export type GameEvent =
  | { type: 'server'; msg: ServerMessage; at: number }
  | { type: 'connecting' }
  | { type: 'closed'; reason?: string }
  /** On vient d'envoyer une reponse : on verrouille sans attendre le retour. */
  | { type: 'submitted' }
  | { type: 'dismissError' }
  | { type: 'reset' };

function fromLobby(state: GameState, lobby: LobbyState): GameState {
  return {
    ...state,
    status: 'joined',
    you: lobby.you,
    room: lobby.room,
    hostId: lobby.hostId,
    players: lobby.players,
    readyIds: lobby.readyIds,
    catalogue: lobby.catalogue,
    loadoutSize: lobby.loadoutSize,
    availableThemes: lobby.availableThemes,
    selectedThemes: lobby.selectedThemes,
    powersEnabled: lobby.powersEnabled,
    phase: lobby.phase,
    roundNumber: lobby.roundNumber,
    totalRounds: lobby.totalRounds,
  };
}

export function reduce(state: GameState, event: GameEvent): GameState {
  switch (event.type) {
    case 'connecting':
      return { ...initialState, status: 'connecting' };

    case 'closed':
      return {
        ...state,
        status: 'closed',
        error: event.reason ?? state.error,
        phaseEndsAt: null,
      };

    case 'submitted':
      return { ...state, submitted: true };

    case 'dismissError':
      return { ...state, error: null };

    case 'reset':
      return { ...initialState };

    case 'server':
      return applyServer(state, event.msg, event.at);
  }
}

function applyServer(state: GameState, msg: ServerMessage, at: number): GameState {
  switch (msg.t) {
    case 'hello':
      return { ...state, protocolVersion: msg.version };

    case 'joined':
      return { ...state, status: 'joined', you: msg.playerId, room: msg.room, error: null };

    case 'lobby':
      return fromLobby(state, msg.state);

    case 'income':
      // Les des arrivent juste avant la fenetre de mise. On les garde pour les
      // faire rouler a l'ecran pendant que le joueur reflechit.
      return { ...state, income: msg.rolls };

    case 'betting': {
      const entering = state.phase !== 'betting';
      return {
        ...state,
        phase: 'betting',
        betting: msg.state,
        roundNumber: msg.state.roundNumber,
        totalRounds: msg.state.totalRounds,
        // Une manche s'ouvre : on efface ce qui appartenait a la precedente.
        round: entering ? null : state.round,
        result: entering ? null : state.result,
        feedback: entering ? null : state.feedback,
        submitted: entering ? false : state.submitted,
        roundToken: entering ? state.roundToken + 1 : state.roundToken,
        phaseEndsAt: at + msg.state.remainingMs,
        phaseTotalMs: entering ? msg.state.remainingMs : state.phaseTotalMs,
      };
    }

    case 'round': {
      const view = msg.view;
      const stageChanged = state.round === null || view.stageIndex !== state.round.stageIndex;
      return {
        ...state,
        phase: 'playing',
        round: view,
        // Le total se deduit de la vue elle-meme : aucune duree de manche n'est
        // codee en dur cote client, un reglage serveur suffit.
        phaseTotalMs: view.elapsedMs + view.remainingMs,
        phaseEndsAt: at + view.remainingMs,
        stageToken: stageChanged ? state.stageToken + 1 : state.stageToken,
        buzzEndsAt:
          view.buzzHolder !== null && view.buzzRemainingMs !== null
            ? at + view.buzzRemainingMs
            : null,
      };
    }

    case 'answered': {
      const feedback: AnswerFeedback = {
        correct: msg.correct,
        delta: msg.delta,
        token: (state.feedback?.token ?? 0) + 1,
      };
      // `exactOptionalPropertyTypes` : une cle absente n'est pas une cle a
      // `undefined`. On ne l'ajoute que si le serveur l'a envoyee.
      if (msg.detail !== undefined) feedback.detail = msg.detail;
      return { ...state, feedback, submitted: true };
    }

    case 'result': {
      const result: ResultView = {
        answer: msg.answer,
        notes: msg.notes,
        standings: msg.standings,
        bailedOut: msg.bailedOut,
      };
      if (msg.subtitle !== undefined) result.subtitle = msg.subtitle;
      if (msg.imageUrl !== undefined) result.imageUrl = msg.imageUrl;
      return {
        ...state,
        phase: 'result',
        result,
        phaseEndsAt: at + msg.remainingMs,
        phaseTotalMs: msg.remainingMs,
      };
    }

    case 'over':
      return {
        ...state,
        phase: 'over',
        finalStandings: msg.standings,
        phaseEndsAt: null,
        phaseTotalMs: null,
      };

    case 'error':
      return { ...state, error: msg.message };
  }
}

// ---------------------------------------------------------------------------
// Lectures derivees
// ---------------------------------------------------------------------------
// Des fonctions, pas des champs : un etat qui stocke ce qu'il peut deduire
// finit toujours par se contredire lui-meme.

export function playerName(state: GameState, id: string): string {
  return state.players.find((p) => p.id === id)?.name ?? 'Inconnu';
}

export function isBot(state: GameState, id: string): boolean {
  return state.players.find((p) => p.id === id)?.bot ?? false;
}

/** L'index de siege donne a chaque joueur une couleur stable. */
export function seatIndex(state: GameState, id: string): number {
  const index = state.players.findIndex((p) => p.id === id);
  return index < 0 ? 0 : index;
}

export function isHost(state: GameState): boolean {
  return state.you !== null && state.you === state.hostId;
}

/** Le classement courant, quelle que soit la phase. */
export function currentStandings(state: GameState): Standing[] {
  return state.finalStandings ?? state.result?.standings ?? state.betting?.standings ?? [];
}
